import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [countdown, setCountdown] = useState(0);

  const { resetPassword } = useAuthStore();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (email) {
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      setEmailValid(isValid);
    } else {
      setEmailValid(null);
    }
  }, [email]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [countdown, canResend]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
      setCanResend(false);
      setCountdown(60);
    } catch (err: any) {
      let errorMessage = 'Une erreur est survenue lors de l\'envoi de l\'email';

      if (err && err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setError('');
    setIsLoading(true);

    try {
      await resetPassword(email);
      setCanResend(false);
      setCountdown(60);
    } catch (err: any) {
      let errorMessage = 'Une erreur est survenue lors de l\'envoi de l\'email';

      if (err && err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-40 right-20 w-48 h-48 bg-gradient-to-br from-blue-400/15 to-cyan-400/15 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '3s' }} />
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-gradient-to-br from-cyan-400/25 to-teal-400/25 rounded-full blur-lg animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 right-1/3 w-36 h-36 bg-gradient-to-br from-blue-400/20 to-teal-400/20 rounded-full blur-xl animate-bounce" style={{ animationDuration: '4s', animationDelay: '2s' }} />

        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-8">
        <div className={`max-w-md w-full transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="relative group">
                <img
                  src="/cresslogo.png"
                  alt="CRESS Guadeloupe"
                  className="h-24 w-auto object-contain transform transition-all duration-500 group-hover:scale-110 drop-shadow-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-cyan-400/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-teal-700 bg-clip-text text-transparent mb-3 animate-fade-in">
              Mot de passe oublié
            </h1>
            <p className="text-gray-600 text-base animate-slide-up" style={{ animationDelay: '0.2s' }}>
              Entrez votre adresse email pour recevoir un lien de réinitialisation
            </p>
          </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 p-8 animate-scale-in" style={{ animationDelay: '0.4s' }}>
            {success ? (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </div>

                <div className="text-center space-y-3">
                  <h2 className="text-2xl font-bold text-gray-900">Email envoyé !</h2>
                  <p className="text-gray-600">
                    Un email de réinitialisation a été envoyé à <span className="font-semibold text-blue-600">{email}</span>
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left space-y-2">
                    <p className="text-sm text-blue-900 font-medium">Étapes suivantes :</p>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Vérifiez votre boîte de réception</li>
                      <li>Cliquez sur le lien dans l'email</li>
                      <li>Créez votre nouveau mot de passe</li>
                    </ol>
                  </div>
                  <p className="text-xs text-gray-500">
                    L'email peut prendre quelques minutes à arriver. Pensez à vérifier vos spams.
                  </p>
                </div>

                <div className="space-y-3">
                  {!canResend && countdown > 0 && (
                    <div className="text-center text-sm text-gray-600">
                      Vous pourrez renvoyer l'email dans {countdown} secondes
                    </div>
                  )}

                  <button
                    onClick={handleResend}
                    disabled={!canResend || isLoading}
                    className="w-full py-3 px-4 border-2 border-blue-200 text-blue-700 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 hover:scale-105 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span>Envoi...</span>
                      </span>
                    ) : (
                      'Renvoyer l\'email'
                    )}
                  </button>

                  <Link
                    to="/login"
                    className="w-full flex items-center justify-center space-x-2 py-3 px-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 transition-all duration-200 hover:scale-105 font-medium shadow-lg"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Retour à la connexion</span>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50/90 backdrop-blur-sm border border-red-200 rounded-xl animate-shake">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <p className="text-red-700 text-sm font-medium">{error}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                    Adresse email
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors group-focus-within:text-blue-500" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:border-gray-300 hover:shadow-md text-gray-900 placeholder-gray-500"
                      placeholder="votre@email.com"
                      autoFocus
                    />
                    {emailValid !== null && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        {emailValid ? (
                          <CheckCircle className="w-5 h-5 text-green-500 animate-scale-in" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500 animate-shake" />
                        )}
                      </div>
                    )}
                  </div>
                  {email && !emailValid && (
                    <p className="text-xs text-red-600 animate-slide-down">Format d'email invalide</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !emailValid}
                  className="w-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 text-white py-4 px-6 rounded-xl hover:from-blue-700 hover:via-cyan-700 hover:to-teal-700 focus:ring-4 focus:ring-blue-500/50 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transform"
                >
                  {isLoading ? (
                    <>
                      <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Envoi en cours...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Envoyer le lien</span>
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center space-x-2">
                  <Link
                    to="/login"
                    className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-all duration-200 hover:underline hover:scale-105"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Retour à la connexion</span>
                  </Link>
                </div>
              </form>
            )}
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500 mb-2">Besoin d'aide ?</p>
            <div className="flex justify-center space-x-4 text-xs">
              <a
                href="mailto:support@cress-guadeloupe.org"
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 hover:underline"
              >
                support@cress-guadeloupe.org
              </a>
              <a
                href="tel:+590590123456"
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 hover:underline"
              >
                05 90 12 34 56
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
